interface GenericInputProps {
  label?: string;
  containerClassName?: string;
  labelClassName?: string;
  inputClassName?: string;
  type?:
    | "text"
    | "email"
    | "password"
    | "number"
    | "tel"
    | "file"
    | "date"
    | "url";
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  error?: string;
  readOnly?: boolean;
}

export const GenericInput: React.FC<GenericInputProps> = ({
  label = "Address",
  containerClassName = "my-4",
  labelClassName = "block text-gray-700",
  inputClassName = "bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500",
  type = "text",
  placeholder = "Address",
  value,
  onChange,
  required = false,
  error,
  readOnly = false,
}) => {
  return (
    <div className={containerClassName}>
      <label className={labelClassName}>{label}</label>
      <input
        type={type}
        placeholder={placeholder}
        className={inputClassName}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        readOnly={readOnly}
      />
      {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
    </div>
  );
};

interface GenericTextareaInputProps {
  label?: string;
  containerClassName?: string;
  labelClassName?: string;
  textareaClassName?: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  error?: string;
  readOnly?: boolean;
  rows?: number;
}

export const GenericTextareaInput: React.FC<GenericTextareaInputProps> = ({
  label = "Description",
  containerClassName = "my-4",
  labelClassName = "block text-gray-700",
  textareaClassName = "bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500",
  placeholder = "Enter text",
  value,
  onChange,
  required = false,
  error,
  readOnly = false,
  rows = 3,
}) => {
  return (
    <div className={containerClassName}>
      <label className={labelClassName}>{label}</label>
      <textarea
        placeholder={placeholder}
        className={textareaClassName}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        readOnly={readOnly}
        rows={rows}
      />
      {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
    </div>
  );
};

interface FileInputProps {
  label?: string;
  containerClassName?: string;
  labelClassName?: string;
  inputClassName?: string;
  placeholder?: string;
  onChange: (file: File) => void;
  required?: boolean;
  error?: string;
}

export const GenericFileInput: React.FC<FileInputProps> = ({
  label = "Upload File",
  containerClassName = "my-4",
  labelClassName = "block text-gray-700",
  inputClassName = "bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500",
  placeholder = "Choose file",
  onChange,
  required = false,
  error,
}) => {
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onChange(file);
    }
  };

  return (
    <div className={containerClassName}>
      <label className={labelClassName}>{label}</label>
      <input
        type="file"
        placeholder={placeholder}
        className={inputClassName}
        onChange={handleFileChange}
        required={required}
      />
      {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
    </div>
  );
};

interface MultipleFileInputProps {
  label?: string;
  containerClassName?: string;
  labelClassName?: string;
  inputClassName?: string;
  placeholder?: string;
  onChange: (files: File[]) => void;
  required?: boolean;
  error?: string;
  accept?: string;
}

export const GenericMultipleFileInput: React.FC<MultipleFileInputProps> = ({
  label = "Upload Files",
  containerClassName = "my-4",
  labelClassName = "block text-gray-700",
  inputClassName = "bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500",
  placeholder = "Choose files",
  onChange,
  required = false,
  error,
  accept,
}) => {
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      onChange(files);
    }
  };

  return (
    <div className={containerClassName}>
      <label className={labelClassName}>{label}</label>
      <input
        type="file"
        multiple
        accept={accept}
        placeholder={placeholder}
        className={inputClassName}
        onChange={handleFileChange}
        required={required}
      />
      {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
    </div>
  );
};
